import { type HealthScore } from 'types/squares.type';
import { formatToUSD } from 'views/SurvivalAnalysis/Helper/Function/helper.function';
import {
  type KPIHeaderValuePair,
  type MaintenanceDataInterface,
} from '../Interfaces/KPI.interface';
import KPICard from './KPICard';
import { useTranslation } from 'react-i18next';

interface KPIsTabProps {
  assetsItems: HealthScore[];
  totalCosts: {
    total_cost: number;
    total_labour_cost: number;
    total_part_cost: number;
  };
  totalTonnage: number;
  totalTonnageOfAllAssets: KPIHeaderValuePair[];
  maintenanceData: MaintenanceDataInterface;
  totalDowntimeSaved: KPIHeaderValuePair[];
  totalCostSaved: KPIHeaderValuePair[];
  totalProductivityImproved: KPIHeaderValuePair[];
  handleKpiCardClick: (cardType: string) => void;
  totalCurrentPmVsCmHrs: {
    pm: number;
    cm: number;
  };
  totalEngineHours: number;
  totalMiles: number;
}
const KPIsTab = ({
  assetsItems,
  totalCosts,
  totalTonnage,
  maintenanceData,
  totalDowntimeSaved,
  totalCostSaved,
  totalProductivityImproved,
  handleKpiCardClick,
  totalCurrentPmVsCmHrs,
  totalEngineHours,
  totalMiles,
  totalTonnageOfAllAssets,
}: KPIsTabProps) => {
  const { t } = useTranslation();
  const totalNumberOfAssets = [{ header: '', value: assetsItems.length }];
  const totalMaintenanceCostOfAllAssets = [
    {
      header: t('cdp_total_label'),
      value: `$${formatToUSD(totalCosts.total_cost, 'USD')}`,
    },
    {
      header: t('per_tonnage'),
      value: totalTonnage
        ? `$${formatToUSD(totalCosts.total_cost / Number(totalTonnage), 'USD')}`
        : `0`,
    },
    {
      header: t('per_engine_hr'),
      value: totalEngineHours
        ? `$${formatToUSD(
            totalCosts.total_cost / Number(totalEngineHours),
            'USD'
          )}`
        : `0`,
    },
    {
      header: t('per_miles'),
      value: totalMiles
        ? `$${formatToUSD(totalCosts.total_cost / Number(totalMiles), 'USD')}`
        : `0`,
    },
  ];
  const totalCostOfPartsReplacement = [
    {
      header: '',
      value: `$${formatToUSD(totalCosts.total_part_cost, 'USD')}`,
    },
  ];
  const totalCostOfLabor = [
    {
      header: '',
      value: `$${formatToUSD(totalCosts.total_labour_cost, 'USD')}`,
    },
  ];
  const maintenanceCount = [
    {
      header: t('cdp_total_label'),
      value: maintenanceData.totalMaintenanceQuantity.toFixed(0),
    },
    {
      header: 'PM',
      value: maintenanceData.pmTotalQuantity.toFixed(0),
    },
    {
      header: 'CM',
      value: maintenanceData.cmTotalQuantity.toFixed(0),
    },
  ];

  const downtime = [
    {
      header: t('scheduled'),
      value: totalCurrentPmVsCmHrs?.pm,
    },
    {
      header: 'unScheduled',
      value: totalCurrentPmVsCmHrs?.cm,
    },
  ];

  return (
    <>
      <KPICard
        title={t('total_number_of_assets')}
        values={totalNumberOfAssets}
      />

      <KPICard title={t('total_tonnage_2')} values={totalTonnageOfAllAssets} />

      <KPICard
        title={t('potential_downtime_savings')}
        values={totalDowntimeSaved}
        totalType="hrs"
      />
      <KPICard title={t('potential_cost_savings')} values={totalCostSaved} />
      <KPICard
        title={t('potential_productivity_improvement')}
        values={totalProductivityImproved}
      />

      <KPICard
        title={t('total_maintenance_cost_of_all_assets')}
        values={totalMaintenanceCostOfAllAssets}
        onClick={() => {
          handleKpiCardClick('totalMaintenanceCost');
        }}
      />
      <KPICard
        title={t('total_cost_of_parts_replacement')}
        values={totalCostOfPartsReplacement}
        onClick={() => {
          handleKpiCardClick('partsReplacement');
        }}
      />
      <KPICard
        title={t('total_cost_of_labor')}
        values={totalCostOfLabor}
        onClick={() => {
          handleKpiCardClick('maintenanceHours');
        }}
      />
      <KPICard
        title={t('work_order_count')}
        values={maintenanceCount}
        onClick={() => {
          handleKpiCardClick('workOrderCount');
        }}
      />
      <KPICard title={t('downtime')} values={downtime} totalType="hrs" />
    </>
  );
};

export default KPIsTab;
